import React from 'react'
import styled from 'styled-components/macro'
import { OLLIE_FINDING_CARD_COPY } from 'src/utils/ollieFindingCards'
import { OLLIE_SNOOZE_CONFIRM_COPY } from 'src/utils/ollieSnoozeConfirm'

/**
 * Up to 3 things Ollie noticed: what he saw (real numbers from the evidence),
 * one button, honest minutes, and "Why?" into the finding's thread. A card
 * put away with "Remind me later" leaves "Hidden until {date} · Undo".
 */
const OllieFindingCards = ({
  cards = [],
  onAction,
  onWhy,
  onSnooze,
  onUndoSnooze,
  disabled = false,
}) => {
  if (!cards.length) return null

  return (
    <List aria-label='What Ollie noticed'>
      {cards.map(card => {
        if (card.snoozed) {
          return (
            <SnoozedRow
              key={card.checkId}
              role='status'
              data-testid='ollie-snoozed-row'
            >
              <span>{card.hiddenUntilLabel}</span>
              {onUndoSnooze && (
                <>
                  <span aria-hidden='true'>·</span>
                  <LinkButton
                    type='button'
                    aria-label={`${OLLIE_SNOOZE_CONFIRM_COPY.undo}: show ${card.title} again`}
                    onClick={() => onUndoSnooze(card)}
                  >
                    {OLLIE_SNOOZE_CONFIRM_COPY.undo}
                  </LinkButton>
                </>
              )}
              {card.undoFailed && (
                <UndoFailed>{OLLIE_SNOOZE_CONFIRM_COPY.undoFailed}</UndoFailed>
              )}
            </SnoozedRow>
          )
        }
        const meta = [card.minutesLabel, card.asOfLabel].filter(Boolean)
        return (
          <Card key={card.checkId} $notice={card.isNotice}>
            <CardTitle>{card.title}</CardTitle>
            {card.noticed && <Noticed>{card.noticed}</Noticed>}
            {meta.length > 0 && <Meta>{meta.join(' · ')}</Meta>}
            <Actions>
              {card.action && (
                <ActionButton
                  type='button'
                  $quiet={card.isNotice}
                  disabled={disabled}
                  onClick={() => onAction(card)}
                >
                  {card.action.label}
                </ActionButton>
              )}
              {card.canAskWhy !== false && (
                <LinkButton
                  type='button'
                  disabled={disabled}
                  onClick={() => onWhy(card)}
                >
                  {OLLIE_FINDING_CARD_COPY.why}
                </LinkButton>
              )}
              {card.canSnooze && onSnooze && (
                <LinkButton
                  type='button'
                  $muted
                  disabled={disabled}
                  onClick={() => onSnooze(card)}
                >
                  {OLLIE_FINDING_CARD_COPY.snooze}
                </LinkButton>
              )}
            </Actions>
          </Card>
        )
      })}
    </List>
  )
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
`

const Card = styled.div`
  background: ${({ theme, $notice }) =>
    $notice ? theme.colours.bgGrey : theme.colours.mayoWhite};
  border: 1px solid ${({ theme }) => theme.colours.panSilver};
  border-left: 3px solid
    ${({ theme, $notice }) =>
      $notice ? theme.colours.panSilver : theme.colours.lettuceGreen};
  border-radius: 10px;
  padding: 12px 14px;
`

const SnoozedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  padding: 2px 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const UndoFailed = styled.span`
  flex-basis: 100%;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const CardTitle = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Noticed = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Meta = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  margin-top: 10px;
`

const ActionButton = styled.button`
  border: 1px solid
    ${({ theme, $quiet }) =>
      $quiet ? theme.colours.panSilver : theme.colours.lettuceGreen};
  background: ${({ theme, $quiet }) =>
    $quiet ? theme.colours.mayoWhite : theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
  color: ${({ theme, $muted }) =>
    $muted ? theme.colours.oliveGrey : theme.colours.bbqBlack};
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

export default OllieFindingCards
